/** Reads for customers, subjects, dossiers and documents (subsystems/commerce/facade/adapters/admin-api/src/admin/parties.rs). */

import type { CallOptions, DecimalString, DossierStatus, ISODateTime, LifecycleState, MissingItem, RecordedReason, WriteOptions } from '../types';
import type { WalletAdjustmentRequest } from '../../lib/walletAdjustment';

/** Calendar date `YYYY-MM-DD` (SQL `DATE`). */
export type ISODate = string;

/** Keyset-paged list envelope used by every list in parties.rs. */
export interface PagedList<T> {
  items: T[];
  /** Pass back as `after` for the next page; null on the last page. */
  next_after: string | null;
}

// ---------------------------------------------------------------------------
// Customers

export type CustomerStatus = 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
export type AccountType = 'INDIVIDUAL' | 'ORGANIZATION';

/** A customer tier (`customer.tiers`): its own price book prices every product for its customers. */
export interface CustomerTier {
  /** `STANDARD`, `RESELLER`, `GOLD` … */
  code: string;
  name_vi: string;
  name_en: string;
  /** A reseller tier ("hạng đại lý"): VNNIC's gTLD report flags its customers' domains as agent-managed. */
  reseller: boolean;
  /** Its customers may send e-mail with their own SMTP settings and templates. */
  custom_email: boolean;
}

export interface CustomerListQuery {
  /** Substring of the display name, an exact e-mail address or an exact customer ID. */
  q?: string;
  status?: CustomerStatus;
  account_type?: AccountType;
  limit?: number;
  after?: string;
}

/** `GET /customers` row (`CustomerSummary`). */
export interface CustomerSummary {
  id: string;
  account_type: AccountType;
  display_name: string;
  /** Masked, e.g. `j***@example.vn`. */
  email_hint: string;
  country_code: string | null;
  tier: string;
  status: CustomerStatus;
  billing_currency: string;
  domain_count: number;
  subject_count: number;
  created_at: ISODateTime;
}

export interface CustomerOverview {
  customer: {
    id: string;
    /** The customer code (4–9 capital letters or digits): the customer's virtual account is the
     * VA prefix + this code (ADR-027 §2). Absent from servers before it existed. */
    code?: string;
    account_type: AccountType;
    display_name: string;
    email_hint: string;
    /** Masked, e.g. `***567`. */
    phone_hint: string | null;
    country_code: string | null;
    tier: string;
    status: CustomerStatus;
    billing_currency: string;
    created_at: ISODateTime;
    updated_at: ISODateTime;
    /** Billing profile; absent from servers before it existed. */
    profile?: CustomerProfileSummary;
    /** The account holder (null: not linked yet). */
    primary_subject_id?: string | null;
    /** The customer asked that their e-mail, phone and name are not sent to the customer-management system. */
    contact_sharing_withheld?: boolean;
  };
  /** The account holder's summary (null: not linked yet). */
  primary_subject?: PrimarySubjectSummary | null;
  counts: { domains: number; subjects: number; users: number };
  users: {
    user_id: string;
    display_name: string;
    /** Masked login e-mail, e.g. `l***@example.vn`. */
    email_hint: string;
    user_kind: UserKind;
    status: string;
    relation: UserRelation;
    linked_at: ISODateTime;
    last_login_at: ISODateTime | null;
  }[];
  /** First 100, newest first. */
  subjects: {
    id: string;
    subject_type: string;
    status: string;
    legal_name: string | null;
    country_code: string | null;
    created_at: ISODateTime;
    domain_count: number;
    latest_verification_status: string | null;
  }[];
  /** First 100 by name. */
  domains: {
    id: string;
    ascii_name: string;
    unicode_name: string;
    lifecycle_state: LifecycleState;
    /** `domain.domains.dossier_state` (free text; usually a dossier status). */
    dossier_state: string;
    registry_expires_at: ISODateTime | null;
    tld: string;
    registry_code: string;
    registry_name: string;
    subject_id: string | null;
    subject_legal_name: string | null;
  }[];
}

export type UserKind = 'STAFF' | 'CLIENT' | 'CUSTOMER';
export type UserRelation = 'OWNER' | 'ADMIN' | 'BILLING' | 'TECHNICAL' | 'READ_ONLY';

/** A wallet or other ledger account with balances on its normal side (exact decimals). */
export interface LedgerBalance {
  id: string;
  code: string;
  account_type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'EXPENSE';
  currency: string;
  status: 'OPEN' | 'FROZEN' | 'CLOSED';
  balance: DecimalString;
  held: DecimalString;
  available: DecimalString;
}

/** `GET /customers/{id}/billing` (needs `billing.read`): wallets and the newest 10 of each record. */
export interface CustomerBilling {
  wallets: LedgerBalance[];
  order_count: number;
  orders: { id: string; status: string; currency: string; total: DecimalString; created_at: ISODateTime; item_count: number; domains: string[] }[];
  invoice_count: number;
  /** Each with what names its order (see `orderLabel`): first domain, item count and placing time. */
  invoices: {
    id: string;
    document_type: 'INVOICE' | 'CREDIT_NOTE';
    series: string;
    number: number;
    currency: string;
    total: DecimalString;
    order_id: string | null;
    issued_at: ISODateTime;
    order_first_domain: string | null;
    order_item_count: number | null;
    order_created_at: ISODateTime | null;
  }[];
  payment_count: number;
  payments: { id: string; provider: string; purpose: 'WALLET_TOPUP' | 'ORDER'; order_id: string | null; amount: DecimalString; currency: string; status: string; created_at: ISODateTime }[];
  /** Net invoiced (invoices minus credit notes) per currency. */
  invoiced: { currency: string; count: number; total: DecimalString }[];
}

/** `GET /customers/{id}/users/{userId}`: one linked user. */
export interface CustomerUserDetail {
  customer_id: string;
  customer_name: string;
  relation: UserRelation;
  linked_at: ISODateTime;
  user: {
    id: string;
    display_name: string;
    email_hint: string;
    user_kind: UserKind;
    status: string;
    mfa_required: boolean;
    mfa_enrolled: boolean;
    locked: boolean;
    created_at: ISODateTime;
    updated_at: ISODateTime;
  };
  last_login_at: ISODateTime | null;
  active_sessions: number;
  /** Every customer account this user can act for. */
  accounts: { customer_id: string; customer_name: string; account_type: AccountType; status: CustomerStatus; relation: UserRelation; linked_at: ISODateTime }[];
  roles: {
    role_code: string;
    role_description: string;
    scope_type: 'GLOBAL' | 'CUSTOMER';
    scope_id: string | null;
    scope_name: string | null;
    granted_at: ISODateTime;
    granted_by_name: string | null;
    revoked_at: ISODateTime | null;
  }[];
}

// ---------------------------------------------------------------------------
// Subjects (detail: `getSubject` → routes/subjects.rs)

export type SubjectType = 'INDIVIDUAL' | 'ORGANIZATION' | 'HOUSEHOLD_BUSINESS' | 'GOVERNMENT_ENTITY' | 'FOREIGN_ENTITY' | 'OTHER';
export type SubjectStatus = 'ACTIVE' | 'MERGED' | 'DEACTIVATED';

export interface SubjectListQuery {
  /** Substring of the current legal name, or an exact subject ID. */
  q?: string;
  subject_type?: SubjectType;
  status?: SubjectStatus;
  customer_id?: string;
  limit?: number;
  after?: string;
}

export interface SubjectSummary {
  id: string;
  subject_type: SubjectType;
  status: SubjectStatus;
  legal_name: string | null;
  country_code: string | null;
  current_version_number: number | null;
  managing_customer_id: string | null;
  managing_customer_name: string | null;
  /** The HostBill client of a subject imported from HostBill no customer manages yet. */
  hostbill_client_id?: number | null;
  latest_verification_status: string | null;
  domain_count: number;
  created_at: ISODateTime;
}

// ---------------------------------------------------------------------------
// Subject create / edit (routes/subject_write.rs): every field VNNIC takes for a registrant.

export type SubjectGender = 'Male' | 'Female';
export type SubjectIdentifierType = 'NATIONAL_ID' | 'PASSPORT' | 'TAX_CODE' | 'ENTERPRISE_CODE';

/** The legal representative of an organisation (subject form): an existing individual subject of
 * the same customer, or a new one with the individual's fields. */
export interface SubjectRepresentativeChoice {
  subject_id?: string;
  new?: SubjectInput;
}

export interface SubjectInput {
  /** Create only. */
  subject_type?: 'INDIVIDUAL' | 'ORGANIZATION' | 'HOUSEHOLD_BUSINESS';
  /** Create only. */
  managing_customer_id?: string;
  /** Individuals: may be empty when first and last name are given (last + " " + first). */
  legal_name: string;
  /** First name ("Tên"): an individual's own, an organisation's contact person's. Edit: absent = keep. */
  first_name?: string | null;
  /** Last name ("Họ"). */
  last_name?: string | null;
  trade_name?: string | null;
  nationality?: string | null;
  /** Edit: absent = keep the date on file. */
  birth_date?: string | null;
  gender?: SubjectGender | null;
  /** Organisations: founding date (YYYY-MM-DD). */
  establish_date?: string | null;
  position?: string | null;
  /** Household business: Mã số đăng ký hộ kinh doanh (create: required; edit: absent = keep). */
  household_code?: string | null;
  /** Household business: its tax code, the owner's CCCD (optional). */
  household_tax_code?: string | null;
  /** Edit: absent = keep the identifier on file. */
  identifier?: { type: SubjectIdentifierType; value: string; issuing_country?: string | null; issued_at?: string | null } | null;
  address: {
    country_code: string;
    street: string;
    province_id?: number | null;
    ward_id?: number | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
  };
  email: string;
  phone: string;
  fax?: string | null;
  /** Edit only (required). */
  reason?: string;
  expected_version?: number;
  /** Organisations: the legal representative (absent: unchanged), linked as LEGAL_REPRESENTATIVE. */
  representative?: SubjectRepresentativeChoice | null;
}

/** `GET /subjects/{id}/profile` (also `profile` of `GET /subjects/{id}`): the values stored for the
 * current version. Identity numbers only as hints, the birth date only as "on file". */
export interface SubjectProfile {
  id: string;
  subject_type: SubjectType;
  version: number;
  managing_customer_id: string | null;
  current_version_id: string;
  version_number: number;
  legal_name: string;
  /** First / last name: an individual's own, an organisation's contact person's. */
  first_name?: string | null;
  last_name?: string | null;
  nationality: string | null;
  country_code: string;
  has_birth_date: boolean;
  gender: SubjectGender | null;
  position: string | null;
  trade_name: string | null;
  /** Organisations: founding date (YYYY-MM-DD). */
  establish_date?: string | null;
  vn_address: { address: string; full_address: string; province_id: number; province_name: string; ward_id: number | null; ward_name: string | null } | null;
  address: { street: string[]; city: string; province: string | null; postal_code: string | null; country_code: string } | null;
  email: string | null;
  phone: string | null;
  fax: string | null;
  identifiers: {
    /** Reveal handle (`POST /subjects/identifiers/{id}/reveal`). */
    id?: string;
    type: string;
    country: string;
    hint: string | null;
    issued_at?: string | null;
    expires_at?: string | null;
  }[];
  created_at?: string;
  change_reason?: string | null;
  /** Every stored address of the version (the first is `address`). */
  addresses?: { address_type: string; street: string[]; city: string; province: string | null; postal_code: string | null; country_code: string; created_at?: string }[];
  /** Stored attributes the fields above do not cover (e.g. organisation data from other channels). */
  other_attributes?: Record<string, unknown>;
  /** Active relationships (legal representative, parent organisation…). */
  relationships?: { type: string; related_subject_id: string; related_name: string | null; valid_from: string; valid_until: string | null }[];
}

/** `GET /customers/{id}` (routes/accounts.rs): the editable values of an account. */
export type NotificationLanguage = 'vi' | 'en';

/** Invoice details ("thông tin xuất hoá đơn"): with `same_as_account` the buyer is the primary
 * subject (its name, its tax code by kind — a person's CCCD, an enterprise's code — its address)
 * and the account e-mail. */
export interface InvoiceDetailsInput {
  same_as_account: boolean;
  buyer_name?: string | null;
  /** By the primary subject's kind: an individual's / household owner's CCCD (12 digits), an enterprise's 10 or 10-3 digits. */
  tax_code?: string | null;
  address?: string | null;
  emails?: string[];
}

/** The account's billing profile (routes/customer_profile.rs); identity data are the primary subject's. */
export interface CustomerProfileInput {
  notification_language: NotificationLanguage | null;
  invoice: InvoiceDetailsInput;
}

/** The account's billing profile on the customer page: personal values only as hints. */
export interface CustomerProfileSummary {
  notification_language: NotificationLanguage | null;
  invoice_same_as_account: boolean;
  invoice_buyer_name: string | null;
  /** A CCCD masked; an enterprise code whole. */
  invoice_tax_code: string | null;
  invoice_address: string | null;
  invoice_email_hints: string[];
}

/** The account holder on the customer page (`GET /customers/{id}/overview`). */
export interface PrimarySubjectSummary {
  id: string;
  subject_type: SubjectType;
  status: string;
  /** Only for callers who may read subjects. */
  legal_name: string | null;
  version_number: number | null;
  country_code: string | null;
  latest_verification_status: string | null;
  representative_id: string | null;
}

export interface CustomerAccount {
  id: string;
  /** The customer code (see `CustomerOverview`); absent from servers before it existed. */
  code?: string;
  /** Follows the primary subject. */
  account_type: AccountType;
  /** The primary subject's name. */
  display_name: string;
  email: string;
  phone: string | null;
  country_code: string | null;
  tier: string;
  status: CustomerStatus;
  billing_currency: string;
  version: number;
  /** Money has moved (ledger, payments, invoices, orders): the billing currency is fixed. */
  has_ledger_activity: boolean;
  /** The account holder; null: not linked yet. */
  primary_subject_id?: string | null;
  /** null: never chosen (notifications by country). */
  notification_language?: NotificationLanguage | null;
  invoice_same_as_account?: boolean;
  invoice_buyer_name?: string | null;
  /** A CCCD masked; an enterprise code whole. */
  invoice_tax_code?: string | null;
  invoice_address?: string | null;
  invoice_emails?: string[];
}

/** `PUT /customers/{id}`: account and billing data only (identity data are edited on the primary
 * subject); every field is the new value; a reason is required and audited. */
export interface CustomerEdit {
  email: string;
  phone: string | null;
  billing_currency: string;
  tier: string;
  status: CustomerStatus;
  /** Absent: the profile is kept as stored. */
  profile?: CustomerProfileInput;
  expected_version: number;
  reason: string;
}

/** Another account with the same e-mail or tax code: a warning, never a refusal. */
export interface CustomerDuplicate {
  field: 'email' | 'tax_code';
  customer_id: string;
  display_name: string;
}

/** `POST /customers` answer. The first password comes back once; a replay says it was shown. */
export interface CustomerCreated {
  id: string;
  warnings: CustomerDuplicate[];
  login: { user_id: string; relation: UserRelation; created: boolean; initial_password: string | null; secret_already_shown?: boolean } | null;
  /** The primary subject and, for an organisation / household business, its representative's subject. */
  subject: { id: string; current_version_id: string; representative?: { id: string } | null };
}

/** `POST /customers/{id}/wallet/adjustments` answer: the entry posted and the wallet after it. */
export interface WalletAdjusted {
  journal_entry_id: string;
  wallet_account_id: string;
  amount: DecimalString;
  currency: string;
  balance: DecimalString;
  available: DecimalString;
  /** The customer's waiting orders a credit paid (every payment is a wallet top-up). */
  orders_funded: string[];
}

/** 202: above 50 000 000 đ a second person approves first; the same request then posts it. */
export interface WalletAdjustmentApprovalRequested {
  status: 'APPROVAL_REQUESTED';
  approval_request_id: string;
  threshold: DecimalString;
}

/** `POST /customers/{id}/users`. */
export interface NewCustomerUser {
  email: string;
  display_name: string;
  relation: UserRelation;
  reason: string;
}

/** A new user's first password comes back once (`initial_password`); a replay says it was shown. */
export interface CustomerUserAdded {
  customer_id: string;
  user_id: string;
  relation: UserRelation;
  /** A new user was created (otherwise an existing customer user was linked). */
  created: boolean;
  initial_password: string | null;
  secret_already_shown?: boolean;
}

/** `POST /customers` (routes/accounts.rs): the account and its primary subject. */
export interface NewCustomer {
  /** The login e-mail (the OWNER sign-in user's, the account's notifications). */
  email: string;
  phone: string | null;
  billing_currency: string;
  /** One of `listCustomerTiers` (default STANDARD). */
  tier?: string | null;
  profile?: CustomerProfileInput;
  /** The primary subject: exactly the body of `POST /subjects`. */
  subject: SubjectInput;
  /** Also create the OWNER sign-in user with the account e-mail. */
  create_login?: boolean;
}

/** One refused field of a `VALIDATION_FAILED` write. */
export interface SubjectFieldError {
  field: string;
  code: string;
}

// ---------------------------------------------------------------------------
// Dossiers

export type DossierType = 'VN_DOMAIN' | 'INTERNATIONAL_GTLD' | 'INTERNATIONAL_NEW_GTLD' | 'INTERNATIONAL_CCTLD' | 'OTHER';

export interface DossierListQuery {
  /** Prefix of the domain's A-label or U-label. */
  domain?: string;
  status?: DossierStatus;
  dossier_type?: DossierType;
  subject_id?: string;
  /** Include SUPERSEDED versions (default: current versions only). */
  include_superseded?: boolean;
  /** The HostBill-style views ("Đã ký số", "Chưa ký số", "Danh sách eKYC", …). */
  view?: DossierView;
  limit?: number;
  after?: string;
}

/** `SIGNED` / `NOT_SIGNED`: the registrar's signature (.vn); `EKYC`: verified by eKYC; `SENSITIVE_KEYWORD`: flagged by staff; `NOT_SYNCED`: .vn, not on VNNIC yet; `VNNIC`: sent to VNNIC. */
export const DOSSIER_VIEWS = ['SIGNED', 'NOT_SIGNED', 'EKYC', 'SENSITIVE_KEYWORD', 'NOT_SYNCED', 'VNNIC'] as const;
export type DossierView = (typeof DOSSIER_VIEWS)[number];

export interface DossierSummary {
  id: string;
  domain_id: string;
  ascii_name: string;
  unicode_name: string;
  customer_id: string;
  customer_name: string | null;
  dossier_version: number;
  dossier_type: DossierType;
  status: DossierStatus;
  subject_id: string | null;
  subject_legal_name: string | null;
  policy_key: string;
  policy_version: string;
  document_count: number;
  /** The registrar's signature on the dossier (absent from older servers). */
  registrar_signed?: boolean;
  verification_method?: string | null;
  sensitive_keyword?: boolean;
  vnnic_sync_status?: string | null;
  verified_at: ISODateTime | null;
  completed_at: ISODateTime | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export interface DossierDetail {
  id: string;
  domain_id: string;
  ascii_name: string;
  unicode_name: string;
  domain_lifecycle_state: LifecycleState;
  customer_id: string;
  customer_name: string | null;
  dossier_version: number;
  dossier_type: DossierType;
  status: DossierStatus;
  subject_id: string | null;
  subject_version_id: string | null;
  subject_version_number: number | null;
  subject_type: SubjectType | null;
  subject_legal_name: string | null;
  verification_id: string | null;
  verification_method: string | null;
  verification_status: string | null;
  verification_expires_at: ISODateTime | null;
  verification_provider: string | null;
  /** Staff member who reviewed the subject verification. */
  verification_reviewer_name: string | null;
  policy_key: string;
  policy_version: string;
  supersedes_dossier_id: string | null;
  supersedes_dossier_version: number | null;
  superseded_by_dossier_id: string | null;
  superseded_by_dossier_version: number | null;
  verified_at: ISODateTime | null;
  completed_at: ISODateTime | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  /** Field names of the registration form only; the values are never returned. */
  registration_form_fields: string[];
  consents: { type: string | null; text_version: string | null; accepted_at: string | null; actor_type: string | null }[];
  documents: {
    binding_id: string;
    purpose: string;
    bound_at: ISODateTime;
    document_id: string;
    document_type: string;
    classification: DocumentClassification;
    document_version_id: string;
    version_number: number;
    mime_type: string;
    size_bytes: number;
    expires_at: ISODate | null;
    verification_status: DocumentVerificationStatus | null;
  }[];
  status_history: {
    from_status: string | null;
    to_status: string;
    missing_items: MissingItem[];
    reason: RecordedReason | null;
    actor_type: string;
    /** Who made the change: a staff member's name, or the customer account a customer user acted for (never the individual user). */
    actor_name: string | null;
    occurred_at: ISODateTime;
  }[];
  /** Every version of this domain's dossier, oldest first. */
  versions: { id: string; dossier_version: number; status: DossierStatus; created_at: ISODateTime; completed_at: ISODateTime | null }[];
}

// ---------------------------------------------------------------------------
// Documents (metadata only)

export type DocumentOwnerType = 'SUBJECT' | 'DOSSIER' | 'CUSTOMER' | 'CASE';
export type DocumentClassification = 'PUBLIC' | 'INTERNAL' | 'CONFIDENTIAL' | 'HIGHLY_SENSITIVE' | 'SECRET';
export type DocumentVerificationStatus = 'PENDING' | 'PASSED' | 'FAILED' | 'INCONCLUSIVE';

export interface DocumentListQuery {
  owner_type?: DocumentOwnerType;
  owner_id?: string;
  document_type?: string;
  classification?: DocumentClassification;
  limit?: number;
  after?: string;
}

export interface DocumentSummary {
  id: string;
  owner_type: DocumentOwnerType;
  owner_id: string;
  /** Subject legal name, dossier domain, customer name or case number. */
  owner_name: string | null;
  document_type: string;
  classification: DocumentClassification;
  current_version_id: string | null;
  current_version_number: number | null;
  mime_type: string | null;
  size_bytes: number | null;
  issued_at: ISODate | null;
  expires_at: ISODate | null;
  uploaded_at: ISODateTime | null;
  version_count: number;
  binding_count: number;
  latest_verification_status: DocumentVerificationStatus | null;
  created_at: ISODateTime;
}

export interface DocumentDetail {
  id: string;
  owner_type: DocumentOwnerType;
  owner_id: string;
  owner_name: string | null;
  document_type: string;
  classification: DocumentClassification;
  current_version_id: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  /** Newest first. */
  versions: {
    id: string;
    version_number: number;
    mime_type: string;
    size_bytes: number;
    /** Hex SHA-256 of the plaintext. */
    sha256: string;
    issued_at: ISODate | null;
    expires_at: ISODate | null;
    uploaded_by_actor_type: string;
    /** Uploader: a staff member's name, or the customer account (never the individual user). */
    uploaded_by_name: string | null;
    uploaded_at: ISODateTime;
    verifications: {
      id: string;
      verification_type: string;
      provider: string | null;
      status: DocumentVerificationStatus;
      /** Decimal string, e.g. "0.98765". */
      score: string | null;
      /** Staff member who reviewed it (manual reviews). */
      reviewer_name: string | null;
      verified_at: ISODateTime | null;
      created_at: ISODateTime;
    }[];
  }[];
  bindings: {
    binding_id: string;
    dossier_id: string;
    document_version_id: string;
    purpose: string;
    bound_at: ISODateTime;
    dossier_status: DossierStatus;
    dossier_version: number;
    domain_id: string;
    ascii_name: string;
    unicode_name: string;
  }[];
}

/** `GET /subjects/{id}/registry-readiness`: the gaps against the rules every domain follows
 * (`baseline`, once) and, per registry, what it needs beyond them (`extra`). */
export interface SubjectRegistryReadiness {
  baseline?: import('../types').RegistrantProblem[];
  registries: {
    registry_code: string;
    registry_name: string;
    zones: string[];
    problems: import('../types').RegistrantProblem[];
    extra?: import('../types').RegistrantProblem[];
  }[];
  /** How the subject is verified for a new operation (owner decision 2026-10-01); absent on older servers. */
  verification?: SubjectVerificationReadiness;
}

/**
 * Owner decision 2026-10-01: an individual is verified by VNNIC eKYC only (a signed declaration
 * no longer counts — "chưa xác minh" for new operations); an organisation (every other type) by
 * its subject declaration ("bản khai chủ thể"), digitally signed or on paper approved by staff,
 * covering its current data.
 */
export interface SubjectVerificationReadiness {
  subject_type: string;
  required: 'EKYC' | 'SUBJECT_DECLARATION';
  /** `NOT_ACCEPTED`: an individual verified only by a signed declaration; `SIGN_AGAIN`: the declared data changed. */
  state: 'VERIFIED' | 'MISSING' | 'NOT_ACCEPTED' | 'SIGN_AGAIN' | 'PENDING';
  basis: string | null;
  verification_id: string | null;
  message: string | null;
}

export interface AccountsApi {
  listCustomers(query: CustomerListQuery, opts?: CallOptions): Promise<PagedList<CustomerSummary>>;
  getCustomerOverview(id: string, opts?: CallOptions): Promise<CustomerOverview>;
  getCustomerBilling(id: string, opts?: CallOptions): Promise<CustomerBilling>;
  getCustomerUser(customerId: string, userId: string, opts?: CallOptions): Promise<CustomerUserDetail>;
  listSubjects(query: SubjectListQuery, opts?: CallOptions): Promise<PagedList<SubjectSummary>>;
  getSubjectProfile(id: string, opts?: CallOptions): Promise<SubjectProfile>;
  /** What each registry with contact checks (e.g. VNNIC for .vn) would refuse in the subject's current data. */
  getSubjectRegistryReadiness(id: string, opts?: CallOptions): Promise<SubjectRegistryReadiness>;
  createCustomer(body: NewCustomer, opts?: WriteOptions): Promise<CustomerCreated>;
  /** Links one of the customer's subjects as its primary subject. */
  setPrimarySubject(customerId: string, body: { subject_id: string; reason: string }, opts?: WriteOptions): Promise<{ id: string; primary_subject_id: string }>;
  /** The tier catalogue (`customer.tiers`), STANDARD first. */
  listCustomerTiers(opts?: CallOptions): Promise<{ items: CustomerTier[] }>;
  /** Other accounts with this e-mail or tax code. */
  findCustomerDuplicates(query: { email?: string; tax_code?: string; exclude?: string }, opts?: CallOptions): Promise<{ items: CustomerDuplicate[] }>;
  getCustomerAccount(id: string, opts?: CallOptions): Promise<CustomerAccount>;
  updateCustomer(id: string, body: CustomerEdit, opts?: WriteOptions): Promise<{ id: string; version: number; changed: string[] }>;
  addCustomerUser(customerId: string, body: NewCustomerUser, opts?: WriteOptions): Promise<CustomerUserAdded>;
  /** "Điều chỉnh số dư" (`billing.adjust`, an MFA-verified session): credits or debits the wallet. */
  adjustCustomerWallet(customerId: string, body: WalletAdjustmentRequest, opts?: WriteOptions): Promise<WalletAdjusted | WalletAdjustmentApprovalRequested>;
  createSubject(body: SubjectInput, opts?: WriteOptions): Promise<{ id: string; current_version_id: string }>;
  newSubjectVersion(id: string, body: SubjectInput, opts?: WriteOptions): Promise<{ id: string; current_version_id: string }>;
  listDossiers(query: DossierListQuery, opts?: CallOptions): Promise<PagedList<DossierSummary>>;
  getDossier(id: string, opts?: CallOptions): Promise<DossierDetail>;
  listDocuments(query: DocumentListQuery, opts?: CallOptions): Promise<PagedList<DocumentSummary>>;
  getDocument(id: string, opts?: CallOptions): Promise<DocumentDetail>;
  /**
   * One version of a document, decrypted (`GET /documents/{id}/versions/{version}/content`):
   * `identity_document.view` and a reason (audited); `download` saves it as an attachment.
   */
  getDocumentContent(id: string, versionId: string, query: { reason: string; download?: boolean }, opts?: CallOptions): Promise<Blob>;
}
